import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  FaUsers, 
  FaChartLine, 
  FaShieldAlt, 
  FaMobileAlt,
  FaDesktop,
  FaTabletAlt,
  FaCheckCircle,
  FaRocket,
  FaGlobe,
  FaLock,
  FaBell,
  FaDatabase,
  FaCloud,
  FaHeadset,
  FaStar,
  FaPlay,
  FaCode,
  FaServer
} from 'react-icons/fa';
import HeroSlider from '../../components/HeroSlider';
import Carousel from '../../components/Carousel';

const Home = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const { scrollY } = useScroll();

  const features = [
    {
      icon: <FaUsers className="text-4xl" />,
      title: "Role-Based Access",
      description: "Secure access control with Admin, Distributor, and User roles with specialized sub-roles.",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      gradient: "from-blue-400 to-blue-600"
    },
    {
      icon: <FaChartLine className="text-4xl" />,
      title: "Real-time Analytics",
      description: "Comprehensive dashboards with live data visualization and reporting.",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      gradient: "from-green-400 to-green-600"
    },
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: "High Security",
      description: "JWT authentication, rate limiting, and comprehensive audit logging.",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      gradient: "from-red-400 to-red-600"
    },
    {
      icon: <FaMobileAlt className="text-4xl" />,
      title: "Mobile Apps",
      description: "Native Android and iOS applications with offline capabilities.",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      gradient: "from-purple-400 to-purple-600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  const platforms = [
    {
      icon: <FaDesktop className="text-3xl" />,
      title: "Web Application",
      description: "React-based modern web interface with responsive design",
      color: "from-blue-500 to-blue-600",
      features: ["Responsive Design", "Real-time Updates", "Cross-browser Support"],
      gradient: "from-blue-400 to-blue-600"
    },
    {
      icon: <FaMobileAlt className="text-3xl" />,
      title: "Android App",
      description: "Native Android application with offline capabilities",
      color: "from-green-500 to-green-600",
      features: ["Offline Mode", "Push Notifications", "Native Performance"],
      gradient: "from-green-400 to-green-600"
    },
    {
      icon: <FaTabletAlt className="text-3xl" />,
      title: "iOS App",
      description: "Native iOS application with seamless integration",
      color: "from-purple-500 to-purple-600",
      features: ["iOS Native", "Biometric Auth", "App Store Ready"],
      gradient: "from-purple-400 to-purple-600"
    }
  ];

  const benefits = [
    { icon: <FaLock />, text: "Secure role-based access control", color: "text-blue-600" },
    { icon: <FaBell />, text: "Real-time notifications and updates", color: "text-green-600" },
    { icon: <FaDatabase />, text: "Comprehensive audit logging", color: "text-purple-600" },
    { icon: <FaGlobe />, text: "Mobile-responsive design", color: "text-indigo-600" },
    { icon: <FaCloud />, text: "Offline capability for mobile apps", color: "text-cyan-600" },
    { icon: <FaChartLine />, text: "Advanced analytics and reporting", color: "text-emerald-600" },
    { icon: <FaDesktop />, text: "Multi-platform support", color: "text-orange-600" },
    { icon: <FaRocket />, text: "High-performance architecture", color: "text-pink-600" }
  ];

  const stats = [
    { number: "10K+", label: "Active Users", icon: <FaUsers />, color: "from-blue-500 to-blue-600" },
    { number: "99.9%", label: "Uptime", icon: <FaServer />, color: "from-green-500 to-green-600" },
    { number: "24/7", label: "Support", icon: <FaHeadset />, color: "from-purple-500 to-purple-600" },
    { number: "50+", label: "Features", icon: <FaCode />, color: "from-orange-500 to-orange-600" }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CEO, TechCorp",
      content: "BTIndia transformed our business operations completely. The analytics are incredible!",
      rating: 5,
      avatar: "👩‍💼"
    },
    {
      name: "Michael Chen",
      role: "CTO, InnovateLab",
      content: "The security features and role-based access control are exactly what we needed.",
      rating: 5,
      avatar: "👨‍💻"
    },
    {
      name: "Emily Rodriguez",
      role: "Operations Manager, GrowthCo",
      content: "Multi-platform support and offline capabilities make it perfect for our mobile workforce.",
      rating: 5,
      avatar: "👩‍🔬"
    }
  ];
      

  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl"></div>
      </div>

      {/* Header Navigation (Single Menu Bar) */}
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8 }}
        className="bg-white/90 backdrop-blur-xl shadow-lg sticky top-0 z-50 border-b border-white/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div 
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-20 h-20 rounded-xl flex items-center justify-center shadow-lg overflow-hidden bg-white">
                <img src="/bt-india-logo.jpeg" alt="BTIndia Logo" className="w-full h-full object-contain" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                BTIndia
              </h1>
            </motion.div>
            <div className="flex space-x-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-xl transition-all duration-300 font-medium shadow-lg"
                >
                  Home
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/login"
                  className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-300 font-medium hover:shadow-lg"
                >
                  Login
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/register"
                  className="border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-xl hover:bg-purple-600 hover:text-white transition-all duration-300 font-medium hover:shadow-lg"
                >
                  Register
                </Link>
              </motion.div>
              {/* Terms & Conditions link removed as requested */}
            </div>
          </div>
        </div>
      </motion.header>


      {/* Hero Section with HeroSlider and Carousel side by side */}
      <section className="flex flex-col md:flex-row items-center justify-center gap-8 py-12">
        <div className="w-full md:w-2/3">
          <HeroSlider />
        </div>
        <div className="w-full md:w-1/3">
          <Carousel />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                  className="text-center group"
                >
                <div className={`bg-gradient-to-r ${stat.color} text-white p-3 sm:p-4 rounded-2xl mb-3 sm:mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {stat.icon}
                  </div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                <div className="text-gray-600 font-semibold text-sm sm:text-lg">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-20"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
              Powerful Features
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed px-4">
              Everything you need for modern business management
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -15, scale: 1.03 }}
                className={`${feature.bgColor} p-6 sm:p-8 rounded-3xl hover:shadow-2xl transition-all duration-500 border border-gray-100 relative overflow-hidden group`}
              >
                <div className={`bg-gradient-to-r ${feature.gradient} text-white p-4 sm:p-6 rounded-2xl mb-4 sm:mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
                  {feature.description}
                </p>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Platforms Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-purple-600/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Multi-Platform Support
            </h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Access your data from anywhere, on any device
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -15, scale: 1.02 }}
                className="bg-white p-10 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 border border-gray-100 relative overflow-hidden group"
              >
                <div className={`bg-gradient-to-r ${platform.gradient} text-white p-8 rounded-2xl mb-8 flex justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl`}>
                  {platform.icon}
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  {platform.title}
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                  {platform.description}
                </p>
                <div className="space-y-4">
                  {platform.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <FaCheckCircle className="text-green-500 text-xl" />
                      <span className="text-gray-700 font-medium text-lg">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              What Our Users Say
            </h2>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Join thousands of satisfied customers who trust BTIndia
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="text-4xl mb-4">{testimonial.avatar}</div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-xl" />
                  ))}
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-bold text-gray-900 text-lg">{testimonial.name}</div>
                  <div className="text-gray-600">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
                Why Choose{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  BTIndia?
                </span>
              </h2>
              <p className="text-2xl text-gray-600 mb-10 leading-relaxed">
                Our platform provides comprehensive solutions for modern business needs 
                with security, scalability, and user experience at the forefront.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index} 
                    className="flex items-center space-x-4 p-4 rounded-2xl hover:bg-white hover:shadow-lg transition-all duration-300"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className={`text-2xl ${benefit.color}`}>
                      {benefit.icon}
                    </div>
                    <span className="text-gray-700 font-semibold text-lg">{benefit.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-600 to-purple-600 p-10 rounded-3xl text-white relative overflow-hidden shadow-2xl"
            >
              <div className="absolute inset-0 bg-white/10"></div>
              <div className="relative z-10">
                <h3 className="text-4xl font-bold mb-6">Ready to Get Started?</h3>
                <p className="mb-10 text-xl leading-relaxed">
                  Join thousands of users who trust BTIndia for their business management needs.
                </p>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/register"
                    className="bg-white text-blue-600 px-10 py-5 rounded-2xl font-bold hover:shadow-xl transition-all duration-300 inline-flex items-center text-xl shadow-lg"
                  >
                    <FaRocket className="mr-3" />
                    Start Free Trial
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-7xl font-bold text-white mb-8">
              Transform Your Business Today
            </h2>
            <p className="text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
              Experience the power of modern business management with BTIndia's comprehensive platform.
            </p>
            <motion.div 
              className="flex flex-col sm:flex-row gap-8 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/register"
                  className="bg-white text-blue-600 px-12 py-6 rounded-2xl text-2xl font-bold hover:shadow-2xl transition-all duration-300 inline-flex items-center shadow-xl"
                >
                  <FaPlay className="mr-3" />
                  Get Started Now
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/login"
                  className="border-3 border-white text-white px-12 py-6 rounded-2xl text-2xl font-bold hover:bg-white hover:text-blue-600 transition-all duration-300 shadow-xl"
                >
                  Sign In
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="bg-gray-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div 
              className="flex items-center justify-center mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="w-32 h-32 rounded-xl flex items-center justify-center shadow-lg overflow-hidden bg-white">
                <img src="/bt-india-logo.jpeg" alt="BTIndia Logo" className="w-full h-full object-contain" />
              </div>
            </motion.div>
            <motion.p 
              className="text-gray-300 mb-10 max-w-3xl mx-auto text-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Empowering businesses with secure, scalable, and user-friendly solutions.
            </motion.p>
            <motion.div 
              className="flex justify-center space-x-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors font-semibold text-lg">
                Login
              </Link>
              <Link to="/register" className="text-gray-300 hover:text-white transition-colors font-semibold text-lg">
                Register
              </Link>
              <Link to="/legal/terms-and-conditions" className="text-gray-300 hover:text-white transition-colors font-semibold text-lg">
                Terms & Conditions
              </Link>
            </motion.div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home; 